import { Injectable, signal } from '@angular/core';

import { HierarchyColumn, HierarchyColumnsByLever } from '../api/models/data-table.model';

type TableHierarchyKey = 'sales-reps' | 'customer-performance' | 'product-performance';
const TABLE_HIERARCHY_KEYS: readonly TableHierarchyKey[] = [
  'sales-reps',
  'customer-performance',
  'product-performance',
];

function isTableHierarchyKey(value: string): value is TableHierarchyKey {
  return TABLE_HIERARCHY_KEYS.includes(value as TableHierarchyKey);
}

@Injectable({ providedIn: 'root' })
export class HierarchyContextService {
  private readonly _hierarchyByTenant = signal<Record<string, HierarchyColumnsByLever | null>>({});
  private readonly _loadingByTenant = signal<Record<string, boolean>>({});

  setHierarchyColumnsFor(tenantKey: string, hierarchy: HierarchyColumnsByLever | null): void {
    this._hierarchyByTenant.update((current) => ({
      ...current,
      [tenantKey]: hierarchy,
    }));
  }

  storeHierarchyColumnsFromApi(
    tenantKey: string,
    response: { hierarchyColumns?: HierarchyColumnsByLever } | null
  ): void {
    this.setHierarchyColumnsFor(tenantKey, response?.hierarchyColumns ?? null);
  }

  hierarchyColumnsFor(tenantKey: string): HierarchyColumnsByLever | null {
    return this._hierarchyByTenant()[tenantKey] ?? null;
  }

  hierarchyColumnsForTable(tenantKey: string, tableId: string): HierarchyColumn[] {
    const tenantHierarchy = this.hierarchyColumnsFor(tenantKey);
    if (!tenantHierarchy) return [];
    if (!isTableHierarchyKey(tableId)) {
      return [];
    }
    switch (tableId) {
      case 'sales-reps':
        return tenantHierarchy.salesRep ?? [];
      case 'customer-performance':
        return tenantHierarchy.customer ?? [];
      case 'product-performance':
        return tenantHierarchy.product ?? [];
    }
  }

  setHierarchyLoadingFor(tenantKey: string, isLoading: boolean): void {
    this._loadingByTenant.update((current) => ({
      ...current,
      [tenantKey]: isLoading,
    }));
  }

  hierarchyLoadingFor(tenantKey: string): boolean {
    return this._loadingByTenant()[tenantKey] ?? false;
  }
}
